import { Link } from 'react-router-dom';
import type { Group } from '@/types';
import { cn } from '@/lib/utils';

interface GroupCardProps {
  group: Group;
  onJoin?: (id: string) => void;
  onAcceptInvite?: (id: string) => void;
}

export function GroupCard({ group, onJoin, onAcceptInvite }: GroupCardProps) {
  const isMember = group.myRole != null && group.myStatus === 'active';
  const isPendingInvite = group.myStatus === 'pending_invite';
  const hasRequested = group.myStatus === 'requested';

  return (
    <article className="bg-card border border-border rounded-xl overflow-hidden transition-all hover:shadow-md">
      {/* Color header bar */}
      <div
        className="h-2"
        style={{ backgroundColor: group.color || '#7c3aed' }}
      />

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <Link to={`/groups/${group.id}`} className="flex-1 min-w-0">
            <h3 className="font-semibold text-card-foreground truncate hover:text-primary transition-colors">
              {group.name}
            </h3>
            {group.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {group.description}
              </p>
            )}
          </Link>

          <div className="flex-shrink-0">
            {isMember ? (
              <span className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
                Joined
              </span>
            ) : isPendingInvite ? (
              <button
                onClick={() => onAcceptInvite?.(group.id)}
                className="text-xs px-3 py-1.5 rounded-full bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
              >
                Accept
              </button>
            ) : hasRequested ? (
              <span className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-600 font-medium">
                Requested
              </span>
            ) : (
              <button
                onClick={() => onJoin?.(group.id)}
                className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Join
              </button>
            )}
          </div>
        </div>

        {/* Hashtags */}
        {group.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {group.hashtags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <UsersIcon className="w-3.5 h-3.5" />
            {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
          </span>
          <span
            className={cn(
              'px-2 py-0.5 rounded-full',
              group.visibility === 'private'
                ? 'bg-yellow-500/10 text-yellow-600'
                : 'bg-green-500/10 text-green-600'
            )}
          >
            {group.visibility === 'private' ? 'Private' : 'Public'}
          </span>
        </div>
      </div>
    </article>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}
